// How many times the viewer may open the Schedule Builder before the promo
// banner retires. Per-device (localStorage): it's a discoverability nudge, not
// account state. Lives here rather than in the agenda block so every entry point
// — the agenda buttons and the /build-schedule deep link — counts identically.
const BUILDER_INTERACTIONS_KEY = 'unbound-schedule-builder-interactions'
export const BUILDER_INTERACTIONS_LIMIT = 3

// Client wrapper over the Schedule Builder endpoints (Task 11): free-text
// preferences CRUD + the SSE-driven build flow. Mirrors useMyEvents.js's
// style — no imports for $fetch/useState/useSmartEventSource, Nuxt
// auto-imports handle resolution.
export const useScheduleBuilder = () => {
    const prefs = useState('schedule-builder-prefs', () => '')
    const state = useState('schedule-builder-state', () => 'prefs') // 'prefs' | 'working' | 'complete' | 'full'
    const progress = useState('schedule-builder-progress', () => '')
    const limitedCapacityCount = useState('schedule-builder-limited-capacity-count', () => 0)
    const generatedCount = useState('schedule-builder-generated-count', () => 0)
    const traceId = useState('schedule-builder-trace-id', () => '')

    // Read on mount rather than at setup — localStorage doesn't exist during SSR.
    const interactions = useState('schedule-builder-interactions', () => 0)
    const loadInteractions = () => {
        try {
            interactions.value = Number(localStorage.getItem(BUILDER_INTERACTIONS_KEY)) || 0
        } catch {
            // Private mode / storage disabled — treat as a first-time viewer.
        }
    }
    // Re-read before incrementing: the builder can be launched from the nav on a page
    // that never mounted the agenda block, where nothing called loadInteractions() and
    // the in-memory count is still 0. Incrementing that would write 1 back over a real
    // count of 2 and hand the viewer their banner back.
    const recordInteraction = () => {
        loadInteractions()
        interactions.value += 1
        try {
            localStorage.setItem(BUILDER_INTERACTIONS_KEY, String(interactions.value))
        } catch {
            // Private mode / storage disabled — the banner just keeps showing.
        }
    }

    // Part of the "clear" testing affordance (schedule-preferences.vue). Clearing
    // saved preferences alone wasn't enough to re-test the builder from the top:
    // the promo banner retires after three opens, and that count is per-device
    // localStorage rather than account state, so it survived the reset and left
    // the banner gone for good on that browser — which is what made this hard to
    // QA (Asana 141204). Resetting the count hands the banner back.
    const resetInteractions = () => {
        interactions.value = 0
        try {
            localStorage.removeItem(BUILDER_INTERACTIONS_KEY)
        } catch {
            // Private mode / storage disabled — nothing was persisted to remove.
        }
    }

    const loadPrefs = async () => {
        const res = await $fetch('/api/v1/schedule/preferences')
        prefs.value = res?.prefs || ''
        return res
    }

    // Returns a suggested prefs draft; does not touch `prefs` — the caller
    // decides whether to apply it (e.g. only when the field is empty).
    const suggestPrefs = async () => {
        return await $fetch('/api/v1/schedule/preferences/suggest', { method: 'POST' })
    }

    const savePrefs = async (text) => {
        const res = await $fetch('/api/v1/schedule/preferences', { method: 'PUT', body: { prefs: text } })
        prefs.value = text
        return res
    }

    // Opens the SSE build stream via the shared SmartEventSource transport.
    // Returns the event source so a caller can abort it if needed.
    const build = () => {
        state.value = 'working'
        progress.value = ''
        traceId.value = ''

        const { track, distinctId } = useScheduleAnalytics()
        track('schedule assistant prefs submitted', { feature: 'schedule-builder' })

        const { SmartEventSource } = useSmartEventSource()
        // Echo the browser PostHog distinct id so the build's server-side
        // $ai_generation events attribute to the same person, exactly as Find a
        // Session does. Omitted entirely when unavailable (consent not granted).
        const did = distinctId()
        const search = new URLSearchParams()
        if (did) search.set('did', did)
        // Sessions the user removed or 👎'd — excluded from the fresh build so it
        // doesn't re-place something they took off their schedule (Asana 687632).
        const excluded = useDismissedSessions().list()
        if (excluded.length) search.set('exclude', excluded.join(','))
        const qs = search.toString()
        const url = qs ? `/api/v1/schedule/build?${qs}` : '/api/v1/schedule/build'
        const eventSource = new SmartEventSource(url)

        eventSource.addEventListener('progress', (event) => {
            try {
                const { message } = JSON.parse(event.data)
                progress.value = message || ''
            } catch (e) {
                // Ignore malformed progress payloads — not fatal to the build.
            }
        })

        // The server sends this before it does any work, so an error still has a
        // trace id to join on. The `complete` payload re-sends it — same value,
        // so the assignment is idempotent.
        eventSource.addEventListener('trace', (event) => {
            try {
                const { traceId: id } = JSON.parse(event.data)
                if (id) traceId.value = id
            } catch (e) {
                // A malformed trace payload only costs us correlation, not the build.
            }
        })

        eventSource.addEventListener('complete', (event) => {
            try {
                const summary = JSON.parse(event.data)
                generatedCount.value = summary?.generatedCount || 0
                limitedCapacityCount.value = summary?.limitedCapacityCount || 0
                // Never clobber a good id from the `trace` event with an empty one —
                // a `complete` that omitted it would otherwise strip the only thing
                // linking these outcome events to their $ai_generation rows.
                traceId.value = summary?.traceId || traceId.value
                state.value = summary?.allDaysFull ? 'full' : 'complete'
                track('schedule assistant completed', {
                    feature: 'schedule-builder',
                    trace_id: traceId.value,
                    generated_count: generatedCount.value,
                    limited_capacity_count: limitedCapacityCount.value,
                    all_days_full: !!summary?.allDaysFull,
                })
            } catch (e) {
                state.value = 'prefs'
            }
        })

        eventSource.addEventListener('error', (event) => {
            try {
                const { traceId: id } = JSON.parse(event?.data || '{}')
                if (id) traceId.value = id
            } catch (e) {
                // SmartEventSource also dispatches a bare transport-level `error`
                // with no data — nothing to parse, and that's fine.
            }
            // Close BEFORE flipping state: SmartEventSource responds to an error by
            // falling back to a 2-second poll against /api/v2/assistants/history,
            // which suppresses the server's `done` forever. Leaving this open means
            // that poll loop outlives the modal and never stops until a reload.
            eventSource?.close()
            state.value = 'prefs'
            track('schedule assistant error shown', { feature: 'schedule-builder', trace_id: traceId.value })
        })

        eventSource.addEventListener('done', () => {
            eventSource.close()
        })

        return eventSource
    }

    const clearGenerated = async () => {
        return await $fetch('/api/v1/schedule/generated', { method: 'DELETE' })
    }

    return {
        prefs,
        state,
        progress,
        traceId,
        limitedCapacityCount,
        generatedCount,
        interactions,
        interactionsLimit: BUILDER_INTERACTIONS_LIMIT,
        loadInteractions,
        recordInteraction,
        resetInteractions,
        loadPrefs,
        suggestPrefs,
        savePrefs,
        build,
        clearGenerated,
    }
}
